import { z } from "zod";
import { zodSortField } from "@/lib/utils";

const billingCodes = ["FDM", "VN", "FU", "VO", "DA", "CS"] as const;

const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;

const dateOnlyField = z
  .string()
  .trim()
  .regex(dateOnlyRegex, "La date doit être au format AAAA-MM-JJ");

const facturationExclusionRuleBaseSchema = z.object({
  brandNumericCode: z
    .string()
    .trim()
    .regex(/^\d$/, "Le code marque numérique doit contenir 1 chiffre"),
  brandAlphabeticCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      /^[A-Z]$/,
      "Le code marque alphabétique doit contenir 1 lettre majuscule",
    ),
  brandAlphabeticLabel: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      /^[A-Z ]+$/,
      "Le libellé marque doit être alphabétique (espaces autorisés)",
    )
    .max(20, "Le libellé marque alphabétique est limité à 20 caractères"),
  saleNature: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      /^[A-Z0-9]$/,
      "La nature de vente doit contenir 1 caractère alphanumérique",
    ),
  billingCode: z.enum(billingCodes, {
    errorMap: () => ({
      message: "Code facturation invalide (FDM, VN, FU, VO, DA, CS)",
    }),
  }),
  effectiveStartDate: dateOnlyField,
  effectiveEndDate: dateOnlyField.optional().or(z.literal("")).nullable(),
});

function withDateRangeValidation<T extends z.ZodTypeAny>(schema: T) {
  return schema.refine(
    (data) => {
      const endDate = (data as { effectiveEndDate?: string | null })
        .effectiveEndDate;
      if (!endDate) return true;
      return (
        endDate >= (data as { effectiveStartDate: string }).effectiveStartDate
      );
    },
    {
      path: ["effectiveEndDate"],
      message:
        "La date de fin d'application doit être supérieure ou égale à la date de début",
    },
  );
}

export const getFacturationExclusionRulesRequestSchema = z.object({
  sort: zodSortField([
    "brandNumericCode",
    "brandAlphabeticCode",
    "saleNature",
    "billingCode",
    "effectiveStartDate",
    "createdAt",
  ]).optional(),
});

export const addFacturationExclusionRuleSchema = withDateRangeValidation(
  facturationExclusionRuleBaseSchema,
).transform((data) => ({
  ...data,
  effectiveEndDate: data.effectiveEndDate || null,
}));

export const editFacturationExclusionRuleSchema = withDateRangeValidation(
  facturationExclusionRuleBaseSchema.extend({
    id: z.number().int().positive(),
    isActive: z.boolean().default(true),
    versionNote: z.string().max(500).optional(),
  }),
).transform((data) => ({
  ...data,
  effectiveEndDate: data.effectiveEndDate || null,
}));

export const deleteFacturationExclusionRuleSchema = z.object({
  id: z.number().int().positive(),
});
